import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Info, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SERVICE_SLUGS, SERVICE_IMAGES, SERVICE_EXTRA_IMAGES, waLink } from "../config";
import { Reveal, Page, Eyebrow, MaskedLine, Tilt3D, Petals } from "../components/Reveal";
import SEO from "../components/SEO";

const ServicoDetalhe = () => {
  const { slug } = useParams();
  const { t } = useLang();
  const svc = t.services[slug];

  if (!svc || !SERVICE_SLUGS.includes(slug)) {
    return <Navigate to="/servicos" replace />;
  }

  const related = SERVICE_SLUGS.filter((s) => s !== slug).slice(0, 3);
  const extraImages = SERVICE_EXTRA_IMAGES[slug] || [];

  return (
    <Page>
      <SEO title={`${svc.name} | Bloom Beauty Concept Hair`} desc={svc.desc} />

      <section className="relative overflow-hidden bg-cream pt-32 pb-16 sm:pt-40" data-testid="service-hero">
        <div aria-hidden="true" className="pointer-events-none absolute -right-28 top-0 h-96 w-96 rounded-full bg-blush/50 blur-3xl" />
        <Petals />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <Link
              to="/servicos"
              data-testid="back-to-services"
              className="mb-7 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-ink/50 transition-colors hover:text-rosedeep"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t.common.backToServices}
            </Link>
            <Eyebrow className="mb-4">{t.servicesPage.eyebrow}</Eyebrow>
            <h1 className="gold-hairline font-serif text-4xl font-semibold leading-[1.08] text-ink sm:text-5xl" data-testid="service-title">
              <MaskedLine delay={0.05}>{svc.name}</MaskedLine>
            </h1>
            <p className="mt-9 max-w-xl text-base leading-relaxed text-ink/65 sm:text-lg">{svc.desc}</p>
            {svc.professional && (
              <p className="mt-4 text-sm font-semibold uppercase tracking-[0.16em] text-rosedeep">
                {svc.professional}
              </p>
            )}
            {svc.note && (
              <p className="mt-6 flex items-start gap-3 rounded-2xl border border-rose/30 bg-blush/30 px-5 py-4 text-sm text-ink/65" data-testid="service-note">
                <Info className="mt-0.5 w-4 h-4 shrink-0 text-rose" />
                {svc.note}
              </p>
            )}
            <a
              href={waLink(svc.waMessage || t.waEval)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="service-cta"
              className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-rosedeep px-7 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white shadow-[0_18px_40px_-14px_rgba(212,122,142,0.85)] transition-all duration-300 hover:bg-ink"
            >
              {svc.cta}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
          <Reveal delay={0.15} className="relative">
            <div aria-hidden="true" className="absolute -inset-3 rounded-[2.2rem] border border-rose/35" />
            <Tilt3D max={5} className="relative">
              <div className="img-zoom overflow-hidden rounded-[2rem] bg-blush/30 shadow-[0_40px_75px_-28px_rgba(168,94,112,0.55)]">
                <img src={SERVICE_IMAGES[slug]} alt={svc.name} className="h-[26rem] w-full object-contain sm:h-[32rem]" data-testid="service-image" />
              </div>
            </Tilt3D>
          </Reveal>
        </div>
      </section>


      {extraImages.length > 0 && (
        <section className="bg-white py-16 sm:py-20" data-testid="service-extra-images">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="grid gap-6 sm:grid-cols-2">
              {extraImages.map((img, i) => (
                <Reveal key={img} delay={i * 0.1}>
                  <div className="img-zoom overflow-hidden rounded-[2rem] bg-blush/30 ring-1 ring-blush shadow-[0_28px_55px_-30px_rgba(168,94,112,0.45)]">
                    <img src={img} alt={`${svc.name} ${i + 2}`} className="h-[24rem] w-full object-contain sm:h-[30rem]" loading="lazy" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {svc.highlights && (
        <section className="bg-white py-16 sm:py-20" data-testid="service-highlights">
          <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:grid-cols-3 sm:px-8">
            {svc.highlights.map((h, i) => (
              <Reveal key={h.t} delay={i * 0.1}>
                <div className="card-lift h-full rounded-3xl bg-gradient-to-b from-blush/40 to-cream p-8 ring-1 ring-blush">
                  <Sparkles className="w-6 h-6 text-rose" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif text-2xl font-semibold uppercase tracking-wide text-rosedeep">{h.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{h.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {svc.process && (
        <section className="bg-white py-16 sm:py-20" data-testid="service-process">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="flex flex-wrap items-center justify-center gap-y-6">
              {svc.process.map((step, i) => (
                <Reveal key={step} delay={i * 0.08} className="flex items-center">
                  <div className="flex flex-col items-center px-4 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-rose/45 bg-blush/25 font-serif text-lg font-semibold text-rosedeep shadow-[0_10px_25px_-12px_rgba(212,122,142,0.6)]">
                      {i + 1}
                    </span>
                    <span className="mt-3 max-w-[7rem] text-xs font-bold uppercase tracking-widest text-ink/70">{step}</span>
                  </div>
                  {i < svc.process.length - 1 && <ArrowRight className="hidden w-4 h-4 text-rose sm:block" />}
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-gradient-to-b from-cream via-blush/20 to-cream py-16 sm:py-24" data-testid="service-items">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal className="mb-10 text-center">
            <h2 className="font-serif text-3xl font-semibold text-ink sm:text-4xl">{svc.name}</h2>
            <div className="mx-auto mt-5 h-0.5 w-20 rounded-full bg-gradient-to-r from-rose to-gold" />
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-2">
            {svc.items.map((item, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06}>
                <div className="flex items-center gap-3 rounded-2xl border border-blush bg-white px-5 py-4 text-[15px] font-medium text-ink/80 transition-all duration-300 hover:border-rose/50 hover:shadow-[0_14px_30px_-16px_rgba(212,122,142,0.5)]">
                  <Check className="w-4 h-4 shrink-0 text-rose" strokeWidth={2.5} />
                  {item}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <p className="text-sm text-ink/55">{t.common.includedNote}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24" data-testid="related-services">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-10">
            <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">{t.servicesPage.eyebrow}</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-3">
            {related.map((r, i) => (
              <Reveal key={r} delay={i * 0.1}>
                <Link
                  to={`/servicos/${r}`}
                  data-testid={`related-${r}`}
                  className="card-lift img-zoom group relative block h-48 overflow-hidden rounded-3xl bg-blush/30 ring-1 ring-blush hover:ring-rose/50"
                >
                  <img src={SERVICE_IMAGES[r]} alt={t.services[r].name} className="h-full w-full object-contain" loading="lazy" />
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-ink/70 to-transparent p-6">
                    <span className="font-serif text-xl font-semibold text-white">{t.services[r].name}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </Page>
  );
};

export default ServicoDetalhe;
